import type { Events } from '@george-web/event-factory';

import type { Account } from '@shared/products-types';
import type { ProductWithHelpers } from '@shared/products-core';
import { getActionsDropdownConfig } from '@shared/products-core';
import { georgeStats, trackEvent2 } from '@shared/statistics';
import { CustomizationService } from '@api-client/products';
import type { CustomizationView } from '@api-client/products';
import { getFeatureConfig } from '@luna/feature-config';

import { gAPIAccountEntitySlice, updateGAPIAccountById } from '../gAPIAccountEntitySlice';
import { isProductPapiCustomizationEnabled } from '../services/isProductPapiCustomizationEnabled';

type trackEvent2Trigger = Events['retailDashboard']['dashboardItemVisibilityChanged']['trigger'];

type PapiProductWithCustomization = {
    _embedded?: {
        customization?: Partial<CustomizationView>;
    };
};

export const changeProductVisibility = async ({
    hidden,
    product,
    trackEvent2Trigger,
}: {
    hidden: boolean;
    product: ProductWithHelpers;
    trackEvent2Trigger: trackEvent2Trigger;
}): Promise<void> => {
    const { additionalData, id } = product;

    const isPAPICustomizationEnabled = isProductPapiCustomizationEnabled(product.registerType);
    const { relyOnGAPIForAggregation, relyOnPAPIEndpointsForData } = getFeatureConfig(
        ({ products }) => products,
    );
    const isPAPIDataLayerWithGAPIAggregation =
        relyOnPAPIEndpointsForData && relyOnGAPIForAggregation;

    const track = (): void => {
        georgeStats.trackEvent('personaliseGeorge', hidden ? 'overview_hide' : 'overview_show');

        trackEvent2('retailDashboard', 'dashboardItemVisibilityChanged', {
            trigger: trackEvent2Trigger,
            itemType: product.derivedType ?? product.productType ?? '',
            itemSubType: product.productSubType,
            visible: !hidden,
        });
    };

    if (isPAPICustomizationEnabled || isPAPIDataLayerWithGAPIAggregation) {
        const config = getActionsDropdownConfig(product.registerType);
        const papiId = product.underlyingCard?.id ?? product.productId ?? id;

        await CustomizationService.putPublicProductsCustomizationHiddenOnOverview({
            id: papiId,
            requestBody: { hiddenOnOverview: hidden },
        }).then(() => {
            gAPIAccountEntitySlice.updateCache({
                ...product,
                additionalData: { ...product.additionalData, hidden },
            } as Account);

            const synthesized = config?.synthesizePapiProduct?.(product) as
                | PapiProductWithCustomization
                | undefined;

            if (synthesized && config?.updateCache) {
                config.updateCache({
                    ...synthesized,
                    _embedded: {
                        ...synthesized._embedded,
                        customization: {
                            ...synthesized._embedded?.customization,
                            hiddenOnOverview: hidden,
                        },
                    },
                });
            }
        });
        track();
        return;
    }

    await updateGAPIAccountById({
        id,
        changes: {
            id,
            additionalData: {
                ...additionalData,
                hidden,
            },
        },
    });
    track();
};
